import * as React from 'react';
import propz from 'propz';
import { Component } from 'react';
import { Tournament } from 'Src/models/tournament';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { AppUser } from 'Src/views/App/App';
import {
  getTeamPlacesLeft,
  isIndividualTournament,
  isMiniTeamTournament,
  isTeamTournament
} from 'Src/helpers/tournament/tournament';
import { GENDER, SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { AGE_GROUPS, AGE_GROUPS_SORTED } from 'Src/consts/school';
import { TournamentTeam } from 'Src/models/tournamentTeam';
import './TournamentTeamForm.scss';

interface Props {
  tournament: Tournament;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  user: AppUser;
  team?: TournamentTeam;
  allSchoolTeams: TournamentTeam[];
  isSubmitting?: boolean;
  submissionError?: string;
}

interface State {}

export class TournamentTeamForm extends Component<Props, State> {
  isTeamExist() {
    const { team } = this.props;
    return typeof team !== 'undefined';
  }

  getTeamSchema() {
    const { tournament } = this.props;
    const maxPlayers = tournament.maxPlayersPerTeam || 0;
    let shape;

    switch (true) {
      case isIndividualTournament(tournament):
        shape = {
          name: Yup.string().required('Required')
        };
        break;
      default:
        shape = {
          name: Yup.string().required('Required'),
          gender: Yup.string().required('Required'),
          age: Yup.number().required('Required')
        };
    }

    if (maxPlayers > 0) {
      shape.numberOfStudents = Yup.number()
        .typeError('Enter a whole number')
        .required('Required')
        .integer('Enter a whole number')
        .min(1, `At least 1 student`)
        .max(maxPlayers, `No more than ${maxPlayers} students`);
    }

    return Yup.object().shape(shape);
  }

  getTeamInitial() {
    const { tournament, user, team } = this.props;
    const tournamentName = propz.get(tournament, ['name'], '');
    const schoolName = propz.get(user, ['activeSchoolName'], '');
    const teamName = propz.get(team, ['name'], '');
    const teamGender = propz.get(team, ['gender'], '');
    const teamAge = propz.get(team, ['ages', '0'], '');

    let teamInitial;

    switch (true) {
      case isIndividualTournament(tournament):
        teamInitial = {
          name: this.isTeamExist() ? teamName : `${schoolName} ${tournamentName}`
        };
        break;

      default:
        teamInitial = {
          name: this.isTeamExist() ? teamName : `${schoolName} ${tournamentName}`,
          gender: this.isTeamExist() ? teamGender : this.getGenderInitial(),
          age: this.isTeamExist() ? teamAge : this.getAgeInitial()
        };
    }

    const maxPlayers = tournament.maxPlayersPerTeam || 0;
    if (maxPlayers > 0) {
      teamInitial.numberOfStudents = this.isTeamExist() ? team.numberOfStudents : '';
    }

    return teamInitial;
  }

  getGenderInitial(): string {
    const { tournament } = this.props;
    const { isMixedTeamsAvailable, gender } = tournament;
    const isGenderMixed = gender === GENDER.MIXED;

    return isGenderMixed && !isMixedTeamsAvailable ? GENDER.MALE_ONLY : gender;
  }

  getAgeInitial(): number {
    const { tournament } = this.props;
    const { ages } = tournament;

    const agesSorted = [...ages].sort((age1, age2) => age1 - age2);
    const ageInitial = propz.get(agesSorted, ['0'], 0);
    return ageInitial;
  }

  renderGenderOptions(): React.ReactNode {
    const { tournament } = this.props;
    const { isMixedTeamsAvailable, gender } = tournament;

    const isGenderMixed = gender === GENDER.MIXED;
    const isGenderMale = gender === GENDER.MALE_ONLY;
    const isGenderFemale = gender === GENDER.FEMALE_ONLY;

    switch (true) {
      case isGenderMale:
        return (
          <option key="gender_option_male" value={GENDER.MALE_ONLY}>
            {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MALE_ONLY}
          </option>
        );
      case isGenderFemale:
        return (
          <option key="gender_option_female" value={GENDER.FEMALE_ONLY}>
            {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE_ONLY}
          </option>
        );
      case isGenderMixed && !isMixedTeamsAvailable:
        return (
          <>
            <option key="gender_option_male" value={GENDER.MALE_ONLY}>
              {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MALE_ONLY}
            </option>
            <option key="gender_option_female" value={GENDER.FEMALE_ONLY}>
              {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE_ONLY}
            </option>
          </>
        );
      default:
        return (
          <>
            <option key="gender_option_female" value={GENDER.MIXED}>
              {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MIXED}
            </option>
          </>
        );
    }
  }

  renderAgeOptions(): React.ReactNode {
    const { tournament, user } = this.props;
    const { ages } = tournament;

    const { activeSchool } = user;
    const { ageGroupsNaming } = activeSchool;
    const ageGroup = propz.get(AGE_GROUPS, [ageGroupsNaming]);
    const ageGroupSorted = propz.get(AGE_GROUPS_SORTED, [ageGroupsNaming]);

    return [...ages]
      .map(age => {
        return {
          value: age,
          text: ageGroup[age]
        };
      })
      .sort((ageObj1, ageObj2) => {
        const sortedAgeIndex1 = ageGroupSorted.indexOf(ageObj1.text);
        const sortedAgeIndex2 = ageGroupSorted.indexOf(ageObj2.text);

        return sortedAgeIndex1 - sortedAgeIndex2;
      })
      .map(ageObj => (
        <option key={ageObj.value} value={Number(ageObj.value)}>
          {ageObj.text}
        </option>
      ));
  }

  isSaveButtonEnabled(values): boolean {
    const { tournament, allSchoolTeams } = this.props;
    const { autoEnrollmentTeamsSettings = [], isAutoEnrollmentTeams } = tournament;

    if (this.isTeamExist()) {
      return true;
    }

    if (!isAutoEnrollmentTeams) {
      return true;
    }

    const maximumNumberTeamsOfSchool = Number(propz.get(tournament, ['maximumNumberTeamsOfSchool'], 0) || 0);
    const isSchoolLimitReached =
      maximumNumberTeamsOfSchool > 0 && allSchoolTeams.length >= maximumNumberTeamsOfSchool;
    if (isSchoolLimitReached) return false;

    if (isIndividualTournament(tournament)) {
      const setting = autoEnrollmentTeamsSettings[0];
      if (!setting) return false;
      const maximumNumberTeams = Number(setting.maximumNumberTeams || 0);
      const currentNumberTeams = Number(setting.currentNumberTeams || 0);
      return maximumNumberTeams === 0 || currentNumberTeams < maximumNumberTeams;
    }

    const age = Number(values.age);
    const gender = values.gender;
    const autoEnrollmentTeamsSetting =
      autoEnrollmentTeamsSettings.find(setting => setting.age === age && setting.gender === gender) ||
      autoEnrollmentTeamsSettings.find(
        setting => setting.age === age && setting.gender === GENDER.MIXED && gender !== GENDER.MIXED
      );

    if (!autoEnrollmentTeamsSetting) return false;

    const maximumNumberTeams = Number(autoEnrollmentTeamsSetting.maximumNumberTeams || 0);
    const teamPlacesAvailable = getTeamPlacesLeft(autoEnrollmentTeamsSettings, age, gender);
    return maximumNumberTeams === 0 || teamPlacesAvailable > 0;
  }

  render() {
    const { tournament, isSubmitting = false, submissionError = '' } = this.props;
    const isTeamOrMiniTeam = isTeamTournament(tournament) || isMiniTeamTournament(tournament);
    const isEdit = this.isTeamExist();

    return (
      <Formik
        initialValues={this.getTeamInitial()}
        validationSchema={this.getTeamSchema()}
        onSubmit={values => this.props.onSubmit(values)}
      >
        {({ touched, errors, values }) => {
          const isSaveEnabled = this.isSaveButtonEnabled(values);
          return (
            <Form className="tournamentTeamForm">
              <header className="tournamentTeamForm__head">
                <span className="tournamentTeamForm__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M19 8v6M16 11h6" />
                  </svg>
                </span>
                <div>
                  <h2>{isEdit ? 'Edit team' : 'Add team'}</h2>
                  <p>{tournament.name}</p>
                </div>
                <button
                  type="button"
                  className="tournamentTeamForm__close"
                  onClick={this.props.onCancel}
                  aria-label="Close"
                  disabled={isSubmitting}
                >
                  ×
                </button>
              </header>

              <div className="tournamentTeamForm__body">
                <div className="tournamentTeamForm__fields">
                  <label className="tournamentTeamForm__field isWide">
                    <span>Team name</span>
                    <Field type="text" name="name" aria-invalid={Boolean(touched.name && errors.name)} />
                    {touched.name && errors.name ? <small>{String(errors.name)}</small> : null}
                  </label>

                  {isTeamOrMiniTeam ? (
                    <label className="tournamentTeamForm__field">
                      <span>Gender</span>
                      <Field component="select" name="gender" aria-invalid={Boolean(touched.gender && errors.gender)}>
                        {this.renderGenderOptions()}
                      </Field>
                      {touched.gender && errors.gender ? <small>{String(errors.gender)}</small> : null}
                    </label>
                  ) : null}

                  {isTeamOrMiniTeam ? (
                    <label className="tournamentTeamForm__field">
                      <span>Age group</span>
                      <Field component="select" name="age" aria-invalid={Boolean(touched.age && errors.age)}>
                        {this.renderAgeOptions()}
                      </Field>
                      {touched.age && errors.age ? <small>{String(errors.age)}</small> : null}
                    </label>
                  ) : null}

                  {tournament.maxPlayersPerTeam > 0 ? (
                    <label className="tournamentTeamForm__field isWide">
                      <span>
                        Number of students <em>Maximum {tournament.maxPlayersPerTeam}</em>
                      </span>
                      <Field
                        type="number"
                        name="numberOfStudents"
                        min={1}
                        max={tournament.maxPlayersPerTeam}
                        aria-invalid={Boolean(touched.numberOfStudents && errors.numberOfStudents)}
                      />
                      {touched.numberOfStudents && errors.numberOfStudents ? (
                        <small>{String(errors.numberOfStudents)}</small>
                      ) : null}
                    </label>
                  ) : null}
                </div>

                {!isSaveEnabled ? (
                  <div className="tournamentTeamForm__error" role="alert">
                    This team category has reached its entry limit.
                  </div>
                ) : null}
                {submissionError ? (
                  <div className="tournamentTeamForm__error" role="alert">
                    {submissionError}
                  </div>
                ) : null}
              </div>

              <footer className="tournamentTeamForm__foot">
                <button
                  type="button"
                  className="tournamentTeamForm__button"
                  onClick={this.props.onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tournamentTeamForm__button isPrimary"
                  disabled={!isSaveEnabled || isSubmitting}
                >
                  {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add team'}
                </button>
              </footer>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

